import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import GroupAdd from "@mui/icons-material/GroupAdd";
import LocalOffer from "@mui/icons-material/LocalOffer";
import RemoveCircle from "@mui/icons-material/RemoveCircle";
import Restore from "@mui/icons-material/Restore";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";

import {
	clearButtonSx,
	destructiveButtonSx,
	destructiveIconSx,
	gradientPrimaryButtonSx,
	selectionRowContainerSx,
} from "../global/components/datagrid/dataGridSelectionRowStyles";

// ─────────────────────────────────────────────────────────────────────────────
// RuleDataGridSelectionRow
//
// Always-visible bottom action bar for the rules grid. Spans the full width
// of the grid container, sits flush against the bottom edge with a hairline
// top border. Bulk actions stay disabled until rows are selected.
//
// Non-destructive action buttons (Activate, Deactivate, Add to group, Tag,
// Restore) use the brand gradient with filled white icons. The destructive
// Delete stays in the red palette.
// ─────────────────────────────────────────────────────────────────────────────

export default function RuleDataGridSelectionRow(props) {
	const {
		visibleRuleCount = 0, loading = false, selectedRules, onDeactivateClickHandler,
		onActivateClickHandler, onTagClickHandler, onGroupClickHandler,
		onDeleteClickHandler, onClearSelectionClickHandler, onRestoreClickHandler,
		isTrash = false, activateIsDisabled, deactivateIsDisabled
	} = props;

	const total = visibleRuleCount;
	const count = (selectedRules ?? []).length;
	const allSelected = total > 0 && count === total;
	const noun = count === 1 ? "rule" : "rules";
	const hasSelection = count > 0;

	const countLabel = loading
		? null
		: hasSelection
			? `${allSelected ? `All ${count}` : count} ${noun} selected`
			: total ? `${total} ${total === 1 ? "rule" : "rules"}` : "No rules";

	return (
		<Box sx={selectionRowContainerSx}>
			<Stack direction="row" sx={{ alignItems: "center", gap: 1.5 }}>
				{loading ? (
					<Skeleton variant="text" width={80} height={20} />
				) : (
					<Typography sx={{ fontSize: 13, fontWeight: 600, color: hasSelection ? "rgba(13, 31, 48, 0.86)" : "rgba(13, 31, 48, 0.45)" }}>
						{countLabel}
					</Typography>
				)}
				{hasSelection ? (
					<>
						<Box sx={{ height: 18, width: "1px", background: "rgba(13, 31, 48, 0.08)" }} />
						<Button
							size="small"
							startIcon={<Close sx={{ fontSize: 16 }} />}
							onClick={onClearSelectionClickHandler}
							sx={clearButtonSx}
						>
							Clear
						</Button>
					</>
				) : null}
			</Stack>

			{isTrash ? (
				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					<Button
						size="small"
						startIcon={<Restore sx={{ fontSize: 16 }} />}
						onClick={onRestoreClickHandler}
						disabled={!hasSelection}
						sx={gradientPrimaryButtonSx}
					>
						Restore
					</Button>
					<Button
						size="small"
						startIcon={<Delete sx={destructiveIconSx} />}
						onClick={onDeleteClickHandler}
						disabled={!hasSelection}
						sx={destructiveButtonSx}
					>
						Delete forever
					</Button>
				</Stack>
			) : (
				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					<Button
						size="small"
						startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
						onClick={onActivateClickHandler}
						disabled={!hasSelection || activateIsDisabled}
						sx={gradientPrimaryButtonSx}
					>
						Activate
					</Button>
					<Button
						size="small"
						startIcon={<RemoveCircle sx={{ fontSize: 16 }} />}
						onClick={onDeactivateClickHandler}
						disabled={!hasSelection || deactivateIsDisabled}
						sx={gradientPrimaryButtonSx}
					>
						Deactivate
					</Button>
					<Button
						size="small"
						startIcon={<GroupAdd sx={{ fontSize: 16 }} />}
						onClick={onGroupClickHandler}
						disabled={!hasSelection}
						sx={gradientPrimaryButtonSx}
					>
						Add to group
					</Button>
					<Button
						size="small"
						startIcon={<LocalOffer sx={{ fontSize: 16 }} />}
						onClick={onTagClickHandler}
						disabled={!hasSelection}
						sx={gradientPrimaryButtonSx}
					>
						Tag
					</Button>
					<Button
						size="small"
						startIcon={<Delete sx={destructiveIconSx} />}
						onClick={onDeleteClickHandler}
						disabled={!hasSelection}
						sx={destructiveButtonSx}
					>
						Delete
					</Button>
				</Stack>
			)}
		</Box>
	);
}
